import _slicedToArray from "/home/bigstep/Public/googlemediapipe/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
/**
 * @license
 * Copyright 2022 Google Inc. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { backend_util } from '../base';
import { Prod } from '../kernel_names';
import { cumprod } from '../ops/cumprod';
import { mul } from '../ops/mul';
import { reshape } from '../ops/reshape';
import { transpose } from '../ops/transpose';
// Gradient for product operation on a single axis.
function prodGradFn_(x, dy, axis) {
  // The gradient tensor (dy) has a set of axes removed, so we create re-shaped
  // versions (of size 1) for the removed axis; this supports broadcasting over
  // those dimensions.
  var expandedYShape = x.shape.slice();
  expandedYShape[axis] = 1;
  // The actual gradient computation.
  var expandedDy = reshape(dy, expandedYShape);
  var xCumProd = cumprod(x, axis, true, false);
  var xCumRevProd = cumprod(x, axis, true, true);
  var dx = mul(xCumProd, xCumRevProd);
  return mul(expandedDy, dx);
}
// Support gradients when the product is done on many axes at once.
// This done py pushing all the axes on which the product is applied into a
// single axis.
function prodsGradFn_(x, dy, axis) {
  // Move all axes for doing prod over to the end of the tensor.
  var xRank = x.shape.length;
  var finalProdAxis = xRank - axis.length;
  var xPermutation = backend_util.getAxesPermutation(axis, xRank);
  var permutedX = x;
  if (xPermutation != null) {
    permutedX = transpose(x, xPermutation);
  }
  // Reshape all the prod dimensions into a single one, and do compute prod
  // gradients on that.
  var newShape = permutedX.shape.slice();
  var removedShape = newShape.splice(xRank - axis.length, axis.length);
  var endPartShape = removedShape.reduce(function (p, c) {
    return p * c;
  }, 1);
  newShape.push(endPartShape);
  var reshapedPermutedX = permutedX.reshape(newShape);
  var prodGrad = prodGradFn_(reshapedPermutedX, dy, finalProdAxis);
  // Undo the re-shaping now we have the dx vector, and permute back to
  // original axes order.
  prodGrad = prodGrad.reshape(permutedX.shape);
  if (xPermutation != null) {
    var undoPermutation = backend_util.getUndoAxesPermutation(xPermutation);
    prodGrad = transpose(prodGrad, undoPermutation);
  }
  return prodGrad;
}
// Running example:
// [
//   [
//     [3.0, 4.0],
//     [5.0, 6.0],
//     [7.0, 8.0]
//   ],
//   [
//     [3.0, 5.0],
//     [0.0, 6.0],
//     [5.0, 6.0]
//   ]
// ]
//
export var prodGradConfig = {
  kernelName: Prod,
  inputsToSave: ['x'],
  gradFunc: function gradFunc(dy, saved, attrs) {
    var _saved = _slicedToArray(saved, 1),
      _x = _saved[0];
    var axis = attrs.axis;
    var axisArr = [];
    if (axis === undefined || axis === null) {
      axisArr = _x.shape.map(function (_, i) {
        return i;
      });
    } else if (typeof axis === 'number') {
      axisArr = [axis];
    } else {
      axisArr = axis;
    }
    return {
      x: function x() {
        return prodsGradFn_(_x, dy, axisArr);
      }
    };
  }
};